import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { VERTICAL_ASPECT_RATIO } from "@faire/retailer-visitor-shared/lib/verticalAspectRatio";
import { getSettingEnablePdpOosEnhancements } from "@faire/retailer-visitor-shared/settings/useSettingEnablePdpOosEnhancements";
import { buildImageSrcSet } from "@faire/retailer-visitor-shared/ui/buildImageSrcSet";
import {
  IOptimizeImageOptions,
  isManuallyCroppedImage,
  optimizeProductImageUrl,
  optimizeProductImageUrlFillVertical,
} from "@faire/web--source/common/images/optimizeImages";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { IVideo } from "@faire/web-api--source/indigofair/data/IVideo";

export type IShowroomProductMedia =
  | IShowroomProductImage
  | IShowroomProductVideo;

export type IShowroomProductImage = {
  image: IImage;
  isVideo: false;
  index: number;
};

export type IShowroomProductVideo = {
  video: IVideo;
  isVideo: true;
  index: number;
};

export const IMAGE_GRID_MAX_IMAGES_VISIBLE = 6;
export const IMAGE_GRID_OUT_OF_STOCK_MAX_IMAGES_VISIBLE = 2;

export const getImageGridMaxImagesVisible = (
  isProductOptionOutOfStock: boolean = false
) => {
  const enablePdpOosEnhancements = getSettingEnablePdpOosEnhancements();
  if (isProductOptionOutOfStock && enablePdpOosEnhancements) {
    return IMAGE_GRID_OUT_OF_STOCK_MAX_IMAGES_VISIBLE;
  }

  return IMAGE_GRID_MAX_IMAGES_VISIBLE;
};

export const imageSource = (
  image: IImage | undefined,
  isSSR: boolean,
  width: number,
  showVerticalImagery: boolean,
  options?: IOptimizeImageOptions,
  isTabletAndAbove: boolean = true
) => {
  if (!image) {
    return EAEAEA_PIXEL;
  }
  if (
    showVerticalImagery &&
    isTabletAndAbove && // Mobile tile is always square
    isManuallyCroppedImage(image)
  ) {
    return optimizeProductImageUrlFillVertical(image, isSSR, width, options);
  } else {
    return optimizeProductImageUrl(image, isSSR, width, {
      ...(showVerticalImagery
        ? { height: Math.round(width / VERTICAL_ASPECT_RATIO) }
        : {}),
      ...options,
    });
  }
};

export function buildProductImageGridSrcSet(
  image: IImage,
  showVerticalImagery: boolean,
  isTabletAndAbove: boolean = true
) {
  const XLARGE_IMAGE_SIZE = 720;

  return buildImageSrcSet((dprOverride) =>
    imageSource(
      image,
      false,
      XLARGE_IMAGE_SIZE,
      showVerticalImagery,
      {
        dprOverride,
      },
      isTabletAndAbove
    )
  );
}
